<script setup lang="ts">
import { AlertCircle, AlertTriangle, Check, Info, X } from 'lucide-vue-next'
import { computed } from 'vue'
import { useToastsInternal } from '@/composables/useToast'

// Renders the global toast queue. Mounted once at the app root
// (App.vue). Application code shouldn't import this directly — it
// fires toasts via useToast() and this component picks them up.

const { toasts, pause, resume, dismiss } = useToastsInternal()

// Screen-reader announcement (#2394). The message is announced from the
// two ALWAYS-MOUNTED live regions below, not from the visible toast divs.
// A live region created together with its content (role + text in one DOM
// mutation, as a v-for'd toast is) is announce-unreliable across AT/browser
// combos; the reliable Status Messages pattern (WCAG 4.1.3) inserts text
// into a region that already exists. Errors go to the assertive alert
// region, everything else to the polite status region.
const politeToasts = computed(() => toasts.value.filter((t) => t.kind !== 'error'))
const assertiveToasts = computed(() => toasts.value.filter((t) => t.kind === 'error'))

function iconFor(kind: string) {
  if (kind === 'success') return Check
  if (kind === 'error') return AlertCircle
  if (kind === 'warning') return AlertTriangle
  return Info
}
</script>

<template>
  <Teleport to="body">
    <div class="toast-container" role="region" :aria-label="$t('uiStemneToastContainer.regionLabel')">
      <!-- Persistent live regions (#2394). Always mounted, so a pushed
           toast inserts its text into an EXISTING region — the reliable
           announcement pattern. aria-atomic=false so only the newly added
           message is read, not the whole region on each change/dismiss. -->
      <div class="toast-live sr-only" role="status" aria-live="polite" aria-atomic="false">
        <span v-for="toast in politeToasts" :key="toast.id">{{ toast.message }}</span>
      </div>
      <div class="toast-live sr-only" role="alert" aria-live="assertive" aria-atomic="false">
        <span v-for="toast in assertiveToasts" :key="toast.id">{{ toast.message }}</span>
      </div>
      <TransitionGroup name="toast" tag="div" class="toast-stack">
        <div
          v-for="toast in toasts"
          :key="toast.id"
          :class="['toast', `toast--${toast.kind}`]"
          @mouseenter="pause(toast.id)"
          @mouseleave="resume(toast.id)"
          @focusin="pause(toast.id)"
          @focusout="resume(toast.id)"
        >
          <component :is="iconFor(toast.kind)" :size="16" class="toast__icon" aria-hidden="true" />
          <!-- aria-hidden: the message is announced by the live regions
               above; hiding this duplicate keeps it out of the a11y tree
               while the action/close buttons stay operable. -->
          <span class="toast__message" aria-hidden="true">{{ toast.message }}</span>
          <button
            v-if="toast.action"
            type="button"
            class="toast__action"
            @click="toast.action.onClick(); dismiss(toast.id)"
          >
            {{ toast.action.label }}
          </button>
          <button
            type="button"
            class="toast__close"
            :aria-label="$t('uiStemneToastContainer.closeLabel')"
            @click="dismiss(toast.id)"
          >
            <X :size="14" aria-hidden="true" />
          </button>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
// Visually-hidden but announced — carries the toast message for the
// persistent live regions (#2394) without occupying visual space.
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.toast-container {
  position: fixed;
  // Lift above the mobile dashboard's fixed bottom nav when present.
  // MobileNav publishes its footprint into --app-bottom-nav-height while
  // the mobile breakpoint is live; it defaults to 0px everywhere else
  // (desktop, booking flow, portal, marketing), so those surfaces keep
  // the plain corner placement. Without this the success toast rendered
  // *inside* the tab bar on phones and operators never saw it.
  bottom: calc(var(--space-6) + var(--app-bottom-nav-height, 0px));
  right: var(--space-6);
  z-index: 9999;
  // Container itself doesn't catch clicks — only the toasts do. Lets
  // page interactions through the gaps between stacked toasts.
  pointer-events: none;
  max-width: 420px;

  @media (max-width: 640px) {
    // Mobile: full-width strip at the bottom, mirroring the way
    // StemneModal slides up from below — same downward axis so the
    // two surfaces feel like one family.
    bottom: calc(var(--space-4) + var(--app-bottom-nav-height, 0px));
    left: var(--space-4);
    right: var(--space-4);
    max-width: none;
  }
}

.toast-stack {
  display: flex;
  // column-reverse so the newest toast appears at the bottom (closest
  // to the user's eye) and pushes older toasts upward.
  flex-direction: column-reverse;
  gap: var(--space-2);
}

.toast {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  background: var(--bg-card);
  border: 1px solid var(--border-stronger);
  border-radius: 12px;
  box-shadow: var(--shadow-overlay);
  font-family: var(--font-sans);
  font-size: var(--fs-body-sm);
  color: var(--fg-1);
  pointer-events: auto;

  // Semantic kinds: tinted surface + matching 3px left accent +
  // tinted icon. All three cues reinforce each other so the toast
  // reads instantly without forcing the eye to find a small icon.
  // Info stays on the neutral card background — it's the default.
  &--success {
    background: var(--bg-success);
    border-left: 3px solid var(--moss);
    .toast__icon { color: var(--moss); }
  }
  &--warning {
    background: var(--bg-warning);
    border-left: 3px solid var(--fg-warning);
    .toast__icon { color: var(--fg-warning); }
  }
  &--error {
    background: var(--bg-error);
    border-left: 3px solid var(--fg-destructive);
    .toast__icon { color: var(--fg-destructive); }
  }
  &--info .toast__icon {
    color: var(--fg-muted);
  }
}

.toast__icon {
  flex: 0 0 auto;
}

.toast__message {
  flex: 1;
  line-height: 1.4;
}

.toast__action {
  background: transparent;
  border: none;
  color: var(--moss);
  font-family: inherit;
  font-size: var(--fs-body-md);
  font-weight: var(--fw-medium);
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;

  &:hover {
    background: var(--bg-page);
    color: var(--moss-hover);
  }
  &:focus-visible {
    outline: 1.5px solid var(--moss);
    outline-offset: 2px;
  }
}

.toast__close {
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: var(--fg-muted);
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;

  &:hover {
    color: var(--fg-1);
    background: var(--bg-page);
  }
  &:focus-visible {
    outline: 1.5px solid var(--moss);
    outline-offset: 2px;
  }
}

// Slide-up + fade entrance, mirror exit. Matches the design system's
// 120ms fade / 160ms ease-out tokens.
.toast-enter-active,
.toast-leave-active {
  transition:
    opacity var(--duration-fade) var(--ease-default),
    transform var(--duration-move) var(--ease-default);
}
.toast-enter-from {
  opacity: 0;
  transform: translateY(8px);
}
.toast-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

@media (prefers-reduced-motion: reduce) {
  .toast-enter-active,
  .toast-leave-active {
    transition: none;
  }
}
</style>
